import { skipToken, useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';

import { apiQuery } from '../../state';
import { getSpaceFromOrgAllContent } from '../Organization/content';

/**
 * Hook to get a space by its ID, first trying to find it in the organization's content
 * and falling back to a direct API call if not found.
 * This hook is designed to work with React Suspense.
 */
export function useSpace({
    organizationId,
    spaceId,
}: {
    organizationId: string;
    spaceId: string;
}): api.Space | null {
    // First try to get the space from organization content
    const { data: allContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId,
        })
    );

    const spaceFromContent = allContent ? getSpaceFromOrgAllContent(allContent, spaceId) : null;

    // If not found in content, fall back to direct API call
    const { data: spaceFromAPI } = useSuspenseQuery(
        apiQuery('getSpaceById', spaceFromContent ? skipToken : { spaceId }, {
            nullOnError: true,
        })
    );

    return spaceFromContent?.item.space ?? spaceFromAPI ?? null;
}
